import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, BookOpen, Users, Truck, Package, FileText, ReceiptText,
  CreditCard, NotebookPen, Wallet, BarChart3, Settings as SettingsIcon,
  LogOut, Menu, X, Search, FileSignature, Repeat, ClipboardList, Landmark,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard },
  { to: "/estimates", label: "Estimates", icon: FileSignature },
  { to: "/invoices", label: "Invoices", icon: FileText },
  { to: "/recurring", label: "Recurring Invoices", icon: Repeat },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList },
  { to: "/bills", label: "Bills", icon: ReceiptText },
  { to: "/expenses", label: "Expenses", icon: CreditCard },
  { to: "/banking", label: "Banking & MoMo", icon: Landmark },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/vendors", label: "Vendors", icon: Truck },
  { to: "/products", label: "Products & Stock", icon: Package },
  { to: "/accounts", label: "Chart of Accounts", icon: BookOpen },
  { to: "/journal", label: "Journal Entries", icon: NotebookPen },
  { to: "/payroll", label: "Payroll", icon: Wallet },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const current = NAV.find((n) => n.to === location.pathname);

  return (
    <div className="min-h-screen bg-background">
      <aside
        data-testid="sidebar"
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#0F172A] text-slate-300 transition-transform duration-200 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-16 items-center justify-between px-5 border-b border-white/10">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded bg-emerald-500 font-display text-sm font-extrabold text-[#0F172A]">
              NB
            </span>
            <span className="font-display text-lg font-bold tracking-tight text-white">NeroBooks</span>
          </Link>
          <button
            data-testid="sidebar-close-button"
            className="lg:hidden text-slate-400"
            onClick={() => setOpen(false)}
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>
        <nav className="flex flex-col gap-0.5 overflow-y-auto px-3 py-4" style={{ maxHeight: "calc(100vh - 8rem)" }}>
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-500/10 text-emerald-400"
                    : "text-slate-400 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <Icon size={17} strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 w-full border-t border-white/10 p-4">
          <p className="truncate text-xs font-semibold text-white">{user?.name}</p>
          <p className="text-xs uppercase tracking-widest text-emerald-400">{user?.role}</p>
        </div>
      </aside>

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              data-testid="sidebar-toggle-button"
              className="lg:hidden text-slate-700"
              onClick={() => setOpen(true)}
              aria-label="Open navigation"
            >
              <Menu size={20} />
            </button>
            <h1 className="font-display text-lg font-bold tracking-tight text-slate-900" data-testid="page-title">
              {current?.label || "NeroBooks"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm text-slate-400 md:flex">
              <Search size={15} />
              <span>UGX · Uganda · VAT 18%</span>
            </div>
            <button
              data-testid="logout-button"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </header>
        <main className="nb-fade p-4 sm:p-6 lg:p-8" data-testid="main-content">{children}</main>
      </div>
      {open && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 lg:hidden" onClick={() => setOpen(false)} />
      )}
    </div>
  );
};
